/**
 * RCD — Lost CD Indemnity form (digitization of paper Indemnity v18.0).
 *
 * Rare flow — used when customer claims a paper CD certificate is lost.
 * Produces an indemnity record and SessionEvent; PDF is rendered by the
 * template engine (placeholder reference to `RCD_CD/Indemnity - Lost CD Agreement v18.0`).
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button, WidgetCard, KVPair, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatLocalDateTime } from '@/utils/time';
import './rcd.css';

interface LostCdFormData {
  certificateNumber: string;
  issueDate: string;
  faceValue: string;
  lastSeenDate: string;
  circumstancesNote: string;
  indemnifierName: string;
  indemnifierPhone: string;
}

const EMPTY_FORM: LostCdFormData = {
  certificateNumber: '',
  issueDate: '',
  faceValue: '',
  lastSeenDate: '',
  circumstancesNote: '',
  indemnifierName: '',
  indemnifierPhone: '',
};

export function LostCdForm(): JSX.Element {
  const { acctId } = useParams<{ acctId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();

  const [form, setForm] = useState<LostCdFormData>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof LostCdFormData>(k: K, v: LostCdFormData[K]): void => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const canSubmit = (): boolean => {
    return (
      form.certificateNumber.trim().length > 0 &&
      form.issueDate.length > 0 &&
      form.indemnifierName.trim().length > 0 &&
      form.circumstancesNote.trim().length >= 10
    );
  };

  const submit = async (): Promise<void> => {
    if (!user || !acctId) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.retail.events.write({
        eventType: 'cd-lost-indemnity-filed',
        eventCategory: 'cif-account',
        severity: 'info',
        actor: user.upn,
        payloadJson: JSON.stringify({ acctId, ...form }),
      });
      setSubmittedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to file indemnity.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rcd-lost-form">
      <div className="rcd-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to CD
        </Button>
        <h1>Lost CD indemnity · {acctId}</h1>
        <Pill variant="muted">Form v18.0 (rare)</Pill>
      </div>

      <WidgetCard title="Certificate details">
        <div className="rcd-lost-form__row">
          <div className="rcd-wizard__field">
            <label htmlFor="lost-cert-num">Certificate number</label>
            <input
              id="lost-cert-num"
              type="text"
              value={form.certificateNumber}
              onChange={(e) => update('certificateNumber', e.target.value)}
            />
          </div>
          <div className="rcd-wizard__field">
            <label htmlFor="lost-issue-date">Issue date</label>
            <input
              id="lost-issue-date"
              type="text"
              placeholder="YYYY-MM-DD"
              value={form.issueDate}
              onChange={(e) => update('issueDate', e.target.value)}
            />
          </div>
          <div className="rcd-wizard__field">
            <label htmlFor="lost-face-value">Face value</label>
            <input
              id="lost-face-value"
              type="number"
              value={form.faceValue}
              onChange={(e) => update('faceValue', e.target.value)}
            />
          </div>
          <div className="rcd-wizard__field">
            <label htmlFor="lost-last-seen">Last seen</label>
            <input
              id="lost-last-seen"
              type="text"
              placeholder="YYYY-MM-DD"
              value={form.lastSeenDate}
              onChange={(e) => update('lastSeenDate', e.target.value)}
            />
          </div>
        </div>
      </WidgetCard>

      <WidgetCard title="Indemnifier (customer)">
        <div className="rcd-lost-form__row">
          <div className="rcd-wizard__field">
            <label htmlFor="lost-indem-name">Name</label>
            <input
              id="lost-indem-name"
              type="text"
              value={form.indemnifierName}
              onChange={(e) => update('indemnifierName', e.target.value)}
            />
          </div>
          <div className="rcd-wizard__field">
            <label htmlFor="lost-indem-phone">Phone</label>
            <input
              id="lost-indem-phone"
              type="text"
              value={form.indemnifierPhone}
              onChange={(e) => update('indemnifierPhone', e.target.value)}
            />
          </div>
        </div>
      </WidgetCard>

      <WidgetCard title="Circumstances of loss">
        <div className="rcd-wizard__field">
          <label htmlFor="lost-circumstances">Describe circumstances (≥10 characters)</label>
          <textarea
            id="lost-circumstances"
            value={form.circumstancesNote}
            onChange={(e) => update('circumstancesNote', e.target.value)}
          />
        </div>
      </WidgetCard>

      {error && (
        <div className="rcd-banner rcd-banner--err" role="alert">
          {error}
        </div>
      )}

      {submittedAt && (
        <div className="rcd-banner rcd-banner--ok" role="status">
          <CheckCircle2 size={16} className="rcd-inline-icon" />
          <div>
            Indemnity filed. PDF generation and RSC signing queued. Filed at{' '}
            <KVPair label="" value={formatLocalDateTime(submittedAt)} />
          </div>
        </div>
      )}

      <div className="rcd-wizard__footer">
        <Button variant="ghost" onClick={() => navigate('..')}>
          Cancel
        </Button>
        <Button variant="primary" disabled={!canSubmit() || submitting} onClick={() => void submit()}>
          {submitting ? 'Filing…' : 'File indemnity & generate form'}
        </Button>
      </div>
    </div>
  );
}
